// frontend/src/pages/Recorrencias/RecorrenciasPage.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Breadcrumb, Table, Tag, Button, Space, Tooltip, Modal, Form, Input, Select, DatePicker, InputNumber, Radio, message, Switch, Row, Col, List, Card, AutoComplete, App, Drawer, Empty, Divider } from 'antd';
import { HomeOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, CalendarOutlined, BankOutlined, CreditCardOutlined, PauseCircleOutlined, StopOutlined, BellOutlined, CheckCircleOutlined, MinusCircleOutlined, SwapOutlined, WalletOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { getRecurrences, getRecurrenceAgenda, createRecurrence, updateRecurrence, deleteRecurrence } from '../../services/recurrenceService';
import { getCategorias } from '../../services/categoryService';
import { getCartoes, getCartoesAdicionais, getFaturasDisponiveis } from '../../services/cardService';
import { getContas } from '../../services/accountService';
import { getDescricaoSuggestions, deleteDescricaoSuggestion } from '../../services/suggestionService';
import { mapDescricaoSuggestions } from '../../utils/descricaoSuggestions';
import { getErrorMessage } from '../../utils/errorHandler';
import { modalPopupContainer, scrollFieldIntoView } from '../../utils/mobileModal';
import { currencyInputProps } from '../../utils/numberInputMask';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useAuth } from '../../contexts/AuthContext';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import MobileLaunchSheet from '../../components/MobileLaunchSheet/MobileLaunchSheet';
import PixCobrancaSetupModal, { needsPixCobrancaSetup } from '../../components/PixCobrancaSetupModal/PixCobrancaSetupModal';
import { getClientes } from '../../services/clienteService';
import { temModoPrestador } from '../../utils/moduloAccess';

dayjs.locale('pt-br');

const moneyInputProps = currencyInputProps({ inputMode: 'decimal' });

const { Title, Text } = Typography;
const { Option, OptGroup } = Select;

const CategoriaOptionLabel = ({ nome, tipo }) => {
    const color = tipo === 'receita' ? '#28a745' : '#dc3545';
    return (
        <Space>
            <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color, marginRight: '8px' }}></span>
            {nome}
        </Space>
    );
};

const FREQ_LABEL = {
    SEMANAL: 'Semanal',
    QUINZENAL: 'Quinzenal',
    MENSAL: 'Mensal',
    ANUAL: 'Anual',
};

const formatBRL = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Detalhe curto da frequência, ex.: "todo dia 10", "todo 10/01", "toda sex."
const freqDetalhe = (record) => {
    const d = dayjs(record.data_inicio);
    if (!d.isValid()) return '';
    switch (record.frequencia) {
        case 'MENSAL': return `todo dia ${d.format('D')}`;
        case 'ANUAL': return `todo ${d.format('DD/MM')}`;
        case 'SEMANAL': return `toda ${d.format('ddd')}`;
        case 'QUINZENAL': return `a cada 15 dias`;
        default: return '';
    }
};

// Texto da regra completa (tooltip da coluna "Próximo lançamento").
const regraTexto = (record) => {
    const partes = [];
    const det = freqDetalhe(record);
    if (det) partes.push(det.charAt(0).toUpperCase() + det.slice(1));
    partes.push(FREQ_LABEL[record.frequencia] || record.frequencia);
    if (record.data_inicio) partes.push(`desde ${dayjs(record.data_inicio).format('DD/MM/YYYY')}`);
    if (record.data_fim) partes.push(`até ${dayjs(record.data_fim).format('DD/MM/YYYY')}`);
    if (record.numero_max_ocorrencias) partes.push(`máx. ${record.numero_max_ocorrencias} ocorrências`);
    return partes.join(' · ');
};

// Dica relativa da próxima ocorrência.
const relativoData = (iso) => {
    const d = dayjs(iso).startOf('day');
    const hoje = dayjs().startOf('day');
    const diff = d.diff(hoje, 'day');
    if (diff < 0) return { label: `atrasado ${Math.abs(diff)} ${Math.abs(diff) === 1 ? 'dia' : 'dias'}`, tone: 'danger' };
    if (diff === 0) return { label: 'hoje', tone: 'warning' };
    if (diff === 1) return { label: 'amanhã', tone: 'warning' };
    if (diff <= 3) return { label: `em ${diff} dias`, tone: 'warning' };
    return { label: `em ${diff} dias`, tone: 'secondary' };
};

const proximaSortValue = (record) => {
    if (!record.ativa) return Number.MAX_SAFE_INTEGER - 1;
    if (!record.proxima_ocorrencia) return Number.MAX_SAFE_INTEGER;
    return dayjs(record.proxima_ocorrencia).valueOf();
};

// --- MODAL REFATORADO ---
const RecorrenciaModal = ({ visible, onCancel, onFinish, initialData, isMobile }) => {
    const { user, temModoPrestador: modoPrestadorCtx } = useAuth();
    const modoPrestador = modoPrestadorCtx ?? temModoPrestador(user);
    const { modal: modalApi, message: messageApi } = App.useApp();
    const [form] = Form.useForm();
    const [targetType, setTargetType] = useState('conta');
    const [categorias, setCategorias] = useState([]);
    const [contas, setContas] = useState([]);
    const [cartoes, setCartoes] = useState([]);
    const [adicionais, setAdicionais] = useState([]);
    const [selectedCartaoId, setSelectedCartaoId] = useState(null);
    const [clientes, setClientes] = useState([]);
    const [faturasDisponiveis, setFaturasDisponiveis] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSavingAndNew, setIsSavingAndNew] = useState(false);
    const [isReceita, setIsReceita] = useState(false);
    const [ehCobranca, setEhCobranca] = useState(false);
    const [isPixSetupOpen, setIsPixSetupOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [repeticaoModo, setRepeticaoModo] = useState('sempre');

    const handleRemoverSugestao = useCallback((sugestao) => {
        modalApi.confirm({
            title: 'Remover sugestão',
            content: `"${sugestao.descricao}" não vai mais aparecer nas sugestões de descrição. Lançamentos já salvos não são afetados.`,
            okText: 'Remover',
            okButtonProps: { danger: true },
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteDescricaoSuggestion(sugestao.id);
                    setSuggestions((prev) => prev.filter((o) => o.id !== sugestao.id));
                    messageApi.success('Sugestão removida.');
                } catch (err) {
                    messageApi.error(getErrorMessage(err, 'Não foi possível remover a sugestão.'));
                }
            },
        });
    }, [modalApi, messageApi]);

    useEffect(() => {
        if (!visible) {
            setSuggestions([]);
            setSearchTerm('');
            return undefined;
        }
        if (searchTerm.trim().length < 2) {
            setSuggestions([]);
            return undefined;
        }
        const delayDebounceFn = setTimeout(async () => {
            const results = await getDescricaoSuggestions(searchTerm.trim());
            setSuggestions(mapDescricaoSuggestions(results, { onDelete: handleRemoverSugestao }));
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, visible, handleRemoverSugestao]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [categoriasRes, contasRes, cartoesRes] = await Promise.all([
                    getCategorias(),
                    getContas(),
                    getCartoes()
                ]);
                setCategorias(categoriasRes);
                setContas(contasRes.results || contasRes);
                setCartoes(cartoesRes.results || cartoesRes);
            } catch (error) {
                console.error("Erro ao buscar dados para o modal:", error);
            } finally {
                setLoading(false);
            }
        };
        if (visible) {
            fetchData();
            if (modoPrestador) {
                getClientes()
                    .then((data) => setClientes(Array.isArray(data) ? data : data?.results || []))
                    .catch(() => setClientes([]));
            } else {
                setClientes([]);
            }
        }
    }, [visible, modoPrestador]);

    useEffect(() => {
        const fetchDependentData = async () => {
            if (targetType === 'cartaocredito' && selectedCartaoId) {
                setLoading(true);
                try {
                    // Na edição, garante que a fatura já selecionada apareça na lista
                    // mesmo que esteja fora da janela padrão de faturas disponíveis.
                    const faturaContextoId =
                        initialData?.target?.id === selectedCartaoId
                            ? initialData?.fatura_inicial_selecionada
                            : null;
                    const [responseAdicionais, responseFaturas] = await Promise.all([
                        getCartoesAdicionais(selectedCartaoId),
                        getFaturasDisponiveis(selectedCartaoId, faturaContextoId)
                    ]);
                    setAdicionais(responseAdicionais);
                    setFaturasDisponiveis(responseFaturas);
                } catch (error) {
                    console.error("Erro ao buscar dados dependentes:", error);
                    setAdicionais([]);
                    setFaturasDisponiveis([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setAdicionais([]);
                setFaturasDisponiveis([]);
            }
        };
        fetchDependentData();
    }, [selectedCartaoId, targetType, initialData]);

    useEffect(() => {
        if (visible) {
            if (initialData) {
                const targetIsConta = initialData.target.tipo === 'conta';
                setTargetType(initialData.target.tipo);
                const categoriaObj = categorias.find(c => c.id === initialData.categoria);
                const isCatReceita = categoriaObj ? categoriaObj.tipo === 'receita' : false;
                setIsReceita(isCatReceita);
                const cobrancaAtiva = initialData.eh_cobranca || false;
                setEhCobranca(cobrancaAtiva);

                let modo = 'sempre';
                if (initialData.data_fim) modo = 'data';
                else if (initialData.numero_max_ocorrencias) modo = 'ocorrencias';
                setRepeticaoModo(modo);

                const initialValues = {
                    ...initialData,
                    data_inicio: initialData.data_inicio ? dayjs(initialData.data_inicio) : null,
                    data_fim: initialData.data_fim ? dayjs(initialData.data_fim) : null,
                    numero_max_ocorrencias: initialData.numero_max_ocorrencias || null,
                    ativa: initialData.ativa !== false,
                    target_type: initialData.target.tipo,
                    target_id: initialData.target.id,
                    eh_cobranca: cobrancaAtiva,
                    nome_devedor: initialData.nome_devedor || '',
                    cliente: initialData.cliente || undefined,
                };

                if (!targetIsConta) {
                    setSelectedCartaoId(initialData.target.id);
                }
                form.setFieldsValue(initialValues);
            } else {
                form.resetFields();
                setTargetType('conta');
                setSelectedCartaoId(null);
                setAdicionais([]);
                setFaturasDisponiveis([]);
                setIsReceita(false);
                setEhCobranca(false);
                setRepeticaoModo('sempre');
                form.setFieldsValue({ target_type: 'conta', frequencia: 'MENSAL', eh_cobranca: false, ativa: true });
            }
        }
    }, [initialData, visible, form, categorias]);

    const handleFaturaChange = () => {
        form.setFieldsValue({ data_inicio: null });
    };

    const buildPayload = (values) => {
        const payload = {
            ...values,
            data_inicio: values.data_inicio.format('YYYY-MM-DD'),
            data_fim: repeticaoModo === 'data' && values.data_fim ? values.data_fim.format('YYYY-MM-DD') : null,
            numero_max_ocorrencias: repeticaoModo === 'ocorrencias' ? (values.numero_max_ocorrencias || null) : null,
            ativa: initialData ? (values.ativa !== false) : true,
            eh_cobranca: isReceita && ehCobranca,
            nome_devedor: (isReceita && ehCobranca) ? values.nome_devedor : null,
            cliente: (isReceita && ehCobranca) ? (values.cliente || null) : null,
        };
        return payload;
    };

    const handleFinishForm = (values) => {
        const payload = buildPayload(values);
        onFinish(payload, true);
    };

    const handleSaveAndNew = async () => {
        try {
            const values = await form.validateFields();
            setIsSavingAndNew(true);
            const payload = buildPayload(values);
            await onFinish(payload, false);
            form.resetFields(['descricao', 'valor', 'categoria', 'associada_ao_cartao', 'data_inicio', 'eh_cobranca', 'nome_devedor']);
            setEhCobranca(false);
            setSearchTerm('');
            setSuggestions([]);
        } catch (errorInfo) {
            console.log('Falha na validação:', errorInfo);
        } finally {
            setIsSavingAndNew(false);
        }
    };

    const handleTargetIdChange = (value) => {
        if (targetType === 'cartaocredito') {
            setSelectedCartaoId(value);
            form.setFieldsValue({ associada_ao_cartao: null, fatura_inicial_selecionada: null, data_inicio: null });
        }
    };

    const applyCategoriaEffects = (value) => {
        const categoriaObj = categorias.find(c => c.id === value);
        if (!categoriaObj) return;
        const isReceitaLocal = categoriaObj.tipo === 'receita';
        setIsReceita(isReceitaLocal);
        if (!isReceitaLocal) {
            setEhCobranca(false);
            form.setFieldsValue({ eh_cobranca: false, nome_devedor: '' });
        }
    };

    const handleCategoriaChange = (value) => applyCategoriaEffects(value);

    const handleDescricaoSearch = (value) => setSearchTerm(value);

    const handleDescricaoSelect = (value, option) => {
        setSearchTerm(value);
        const patch = { descricao: option.value };
        if (option.categoria_id != null) patch.categoria = option.categoria_id;
        form.setFieldsValue(patch);
        if (option.categoria_id != null) applyCategoriaEffects(option.categoria_id);
    };

    const groupedCategorias = useMemo(() => {
        const filteredCategorias = targetType === 'cartaocredito' ? categorias.filter(c => c.tipo === 'despesa') : categorias;
        return filteredCategorias.reduce((acc, cat) => {
            const tipo = cat.tipo === 'receita' ? 'Receitas' : 'Despesas';
            if (!acc[tipo]) acc[tipo] = [];
            acc[tipo].push(cat);
            return acc;
        }, {});
    }, [categorias, targetType]);

    const handleTargetTypeChange = (e) => {
        const newType = e.target.value;
        setTargetType(newType);
        form.setFieldsValue({ target_id: null, associada_ao_cartao: null, fatura_inicial_selecionada: null, categoria: null, data_inicio: null });
        setSelectedCartaoId(null);
    };

    const frequenciaOptions = [
        { value: 'SEMANAL', label: 'Semanal' },
        { value: 'QUINZENAL', label: 'Quinzenal' },
        { value: 'MENSAL', label: 'Mensal' },
        { value: 'ANUAL', label: 'Anual' },
    ];

    const filteredFrequenciaOptions = targetType === 'cartaocredito'
        ? frequenciaOptions.filter(opt => opt.value === 'MENSAL' || opt.value === 'ANUAL')
        : frequenciaOptions;

    // Configurações de Tamanho
    const inputSize = isMobile ? "large" : undefined; // undefined usa o padrão 'middle' do AntD (igual produção)

    // --- RENDERIZAÇÃO ---

    // Footer Desktop: botões padrão do sistema
    const renderDesktopFooter = () => [
        <Button key="back" onClick={onCancel} disabled={loading || isSavingAndNew}>
            Cancelar
        </Button>,
        !initialData && (
            <Button key="saveAndNew" type="default" loading={isSavingAndNew} onClick={handleSaveAndNew} disabled={loading}>
                Salvar e Adicionar Outro
            </Button>
        ),
        <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()} disabled={isSavingAndNew}>
            {initialData ? "Salvar Alterações" : "Salvar"}
        </Button>,
    ];

    const formContent = (
            <Form form={form} layout="vertical" onFinish={handleFinishForm} initialValues={{ frequencia: 'MENSAL', target_type: 'conta' }}>
                <Form.Item name="target_type" label="Lançar em">
                    <Radio.Group
                        onChange={handleTargetTypeChange}
                        // No mobile, forçamos largura total. No desktop, deixamos o padrão.
                        style={isMobile ? { width: '100%', display: 'flex' } : {}}
                    >
                        <Radio.Button
                            value="conta"
                            style={isMobile ? { flex: 1, textAlign: 'center', padding: '0 4px', fontSize: '13px', whiteSpace: 'nowrap' } : {}}
                        >
                            Conta Bancária
                        </Radio.Button>
                        <Radio.Button
                            value="cartaocredito"
                            style={isMobile ? { flex: 1, textAlign: 'center', padding: '0 4px', fontSize: '13px', whiteSpace: 'nowrap' } : {}}
                        >
                            Cartão de Crédito
                        </Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Por favor, insira a descrição!' }]}>
                    <AutoComplete
                        options={suggestions}
                        onSearch={handleDescricaoSearch}
                        onSelect={handleDescricaoSelect}
                        filterOption={false}
                        placeholder="Ex: Aluguel, Salário, Netflix"
                        size={inputSize}
                        getPopupContainer={modalPopupContainer}
                    >
                        <Input size={inputSize} onFocus={scrollFieldIntoView} />
                    </AutoComplete>
                </Form.Item>

                <Form.Item name="valor" label="Valor" rules={[{ required: true, message: 'Valor é obrigatório' }]}>
                    <InputNumber
                        {...moneyInputProps}
                        placeholder="R$ 0,00"
                        size={inputSize}
                        onFocus={scrollFieldIntoView}
                    />
                </Form.Item>

                <Form.Item name="categoria" label="Categoria" rules={[{ required: true, message: 'Por favor, selecione uma categoria!' }]}>
                    <Select
                        placeholder="Selecione uma categoria"
                        showSearch
                        size={inputSize}
                        onChange={handleCategoriaChange}
                        getPopupContainer={modalPopupContainer}
                        filterOption={(input, option) => (option.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    >
                        {Object.entries(groupedCategorias).map(([tipo, cats]) => (
                            <OptGroup label={tipo} key={tipo}>
                                {cats.map(cat => (
                                    <Option key={cat.id} value={cat.id} label={cat.nome}>
                                        <CategoriaOptionLabel nome={cat.nome} tipo={cat.tipo} />
                                    </Option>
                                ))}
                            </OptGroup>
                        ))}
                    </Select>
                </Form.Item>

                {targetType === 'conta' && isReceita && (
                    <div style={{ marginBottom: 16, background: 'var(--color-surface-muted, #F8F4EC)', padding: '12px 16px', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: ehCobranca ? 12 : 0, gap: 12 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500, color: '#333', fontSize: '13px' }}>
                                    É uma cobrança recorrente?
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #5A6A6A)', marginTop: 2 }}>
                                    Libera lembrete no WhatsApp com Pix a cada vencimento
                                </div>
                            </div>
                            <Form.Item name="eh_cobranca" valuePropName="checked" noStyle>
                                <Switch
                                    checkedChildren="Sim"
                                    unCheckedChildren="Não"
                                    onChange={(checked) => {
                                        setEhCobranca(checked);
                                        if (checked && needsPixCobrancaSetup(user)) {
                                            setIsPixSetupOpen(true);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </div>
                        {ehCobranca && (
                            <>
                                {modoPrestador && (
                                    <Form.Item name="cliente" label="Cliente" style={{ marginBottom: 12 }}>
                                        <Select
                                            allowClear
                                            showSearch
                                            placeholder="Selecionar cliente (opcional)"
                                            optionFilterProp="children"
                                            size={inputSize}
                                            getPopupContainer={modalPopupContainer}
                                            onChange={(id) => {
                                                const found = clientes.find((c) => c.id === id);
                                                if (found) form.setFieldsValue({ nome_devedor: found.nome });
                                            }}
                                        >
                                            {clientes.map((c) => (
                                                <Option key={c.id} value={c.id}>{c.nome}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                )}
                                <Form.Item name="nome_devedor" label="Nome do Pagador" rules={[{ required: true, message: 'Informe quem deve pagar.' }]} style={{ marginBottom: 0 }}>
                                    <Input placeholder="Ex: Cliente Mensalista" size={inputSize} onFocus={scrollFieldIntoView} />
                                </Form.Item>
                            </>
                        )}
                    </div>
                )}

                <Form.Item name="target_id" label="Destino" rules={[{ required: true, message: 'Por favor, selecione o destino!' }]}>
                    <Select
                        placeholder={`Selecione um(a) ${targetType === 'conta' ? 'Conta' : 'Cartão'}`}
                        onChange={handleTargetIdChange}
                        showSearch
                        size={inputSize}
                        getPopupContainer={modalPopupContainer}
                        filterOption={(input, option) => (option.children ?? '').toLowerCase().includes(input.toLowerCase())}
                    >
                        {targetType === 'conta'
                            ? contas.map(c => (
                                <Option key={c.id} value={c.id}>
                                    {!isReceita
                                        ? `${c.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(c.saldo_atual)})`
                                        : c.nome}
                                </Option>
                            ))
                            : cartoes.map(c => <Option key={c.id} value={c.id}>{c.nome}</Option>)}
                    </Select>
                </Form.Item>

                {targetType === 'cartaocredito' && (
                    <>
                        {adicionais.length > 0 && (
                            <Form.Item name="associada_ao_cartao" label="Portador">
                                <Select placeholder="Selecione o portador" allowClear size={inputSize} getPopupContainer={modalPopupContainer}>
                                    <Option value={null}>Titular</Option>
                                    {adicionais.map(ad => <Option key={ad.id} value={ad.id}>{ad.portador}</Option>)}
                                </Select>
                            </Form.Item>
                        )}
                        <Form.Item name="fatura_inicial_selecionada" label="Lançar 1ª recorrência na fatura" rules={[{ required: true, message: 'Selecione a fatura inicial!' }]}>
                            <Select placeholder="Selecione a fatura" loading={loading} disabled={!selectedCartaoId || loading} onChange={handleFaturaChange} size={inputSize} getPopupContainer={modalPopupContainer}>
                                {faturasDisponiveis.map(f => <Option key={f.id} value={f.id}>{f.nome}</Option>)}
                            </Select>
                        </Form.Item>
                    </>
                )}

                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="frequencia" label="Frequência" rules={[{ required: true }]}>
                            <Select size={inputSize} getPopupContainer={modalPopupContainer}>
                                {filteredFrequenciaOptions.map(opt => (
                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item
                            name="data_inicio"
                            label={targetType === 'cartaocredito' ? "Data na Fatura" : "Início"}
                            rules={[{ required: true, message: 'Data obrigatória!' }]}
                        >
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Selecionar" size={inputSize} getPopupContainer={modalPopupContainer} onFocus={scrollFieldIntoView} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item label="Repetir">
                    <Radio.Group
                        value={repeticaoModo}
                        onChange={(e) => setRepeticaoModo(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                        style={isMobile ? { width: '100%', display: 'flex' } : {}}
                    >
                        <Radio.Button value="sempre" style={isMobile ? { flex: 1, textAlign: 'center' } : {}}>Sempre</Radio.Button>
                        <Radio.Button value="data" style={isMobile ? { flex: 1, textAlign: 'center' } : {}}>Até uma data</Radio.Button>
                        <Radio.Button value="ocorrencias" style={isMobile ? { flex: 1, textAlign: 'center' } : {}}>Nº de vezes</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                {repeticaoModo === 'data' && (
                    <Form.Item name="data_fim" label="Encerrar em" rules={[{ required: true, message: 'Informe a data de término.' }]}>
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Selecionar" size={inputSize} getPopupContainer={modalPopupContainer} onFocus={scrollFieldIntoView} />
                    </Form.Item>
                )}
                {repeticaoModo === 'ocorrencias' && (
                    <Form.Item name="numero_max_ocorrencias" label="Total de ocorrências" rules={[{ required: true, message: 'Informe o número de ocorrências.' }, { type: 'number', min: 1, message: 'Deve ser pelo menos 1.' }]}>
                        <InputNumber min={1} step={1} style={{ width: '100%' }} placeholder="Ex: 12" size={inputSize} onFocus={scrollFieldIntoView} />
                    </Form.Item>
                )}

                {initialData && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, background: 'var(--color-surface-muted, #F8F4EC)', padding: '12px 16px', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, color: '#333', fontSize: '13px' }}>Recorrência ativa</div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #5A6A6A)', marginTop: 2 }}>
                                Pausada, ela deixa de projetar novos lançamentos e sai do resumo, sem ser excluída.
                            </div>
                        </div>
                        <Form.Item name="ativa" valuePropName="checked" noStyle initialValue>
                            <Switch checkedChildren="Ativa" unCheckedChildren="Pausada" />
                        </Form.Item>
                    </div>
                )}
            </Form>
    );

    const pixSetup = (
        <PixCobrancaSetupModal
            open={isPixSetupOpen}
            onClose={() => setIsPixSetupOpen(false)}
            isMobile={isMobile}
        />
    );

    if (isMobile) {
        return (
            <>
                <MobileLaunchSheet
                    open={visible}
                    title={initialData ? 'Editar Recorrência' : 'Nova Recorrência'}
                    onCancel={onCancel}
                    onSave={() => form.submit()}
                    saveLabel="Salvar"
                    saveLoading={loading}
                    saveDisabled={isSavingAndNew}
                    cancelDisabled={loading || isSavingAndNew}
                    secondaryAction={
                        !initialData ? (
                            <Button
                                type="default"
                                loading={isSavingAndNew}
                                onClick={handleSaveAndNew}
                                disabled={loading}
                                block
                                size="large"
                            >
                                Salvar e Adicionar Outro
                            </Button>
                        ) : null
                    }
                >
                    {formContent}
                </MobileLaunchSheet>
                {pixSetup}
            </>
        );
    }

    return (
        <>
        <Modal
            title={initialData ? "Editar Recorrência" : "Nova Recorrência"}
            open={visible}
            onCancel={onCancel}
            destroyOnClose
            width={520}
            footer={renderDesktopFooter()}
        >
            {formContent}
        </Modal>
        {pixSetup}
        </>
    );
};

const RecorrenciasPage = () => {
    const navigate = useNavigate();
    const [recorrencias, setRecorrencias] = useState([]);
    const [resumo, setResumo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [categoriaTipoMap, setCategoriaTipoMap] = useState(new Map());
    const [agenda, setAgenda] = useState({ open: false, loading: false, data: null, record: null });
    const { isPrivate } = usePrivacy();
    const [messageApi, contextHolder] = message.useMessage();
    const [modal, modalContextHolder] = Modal.useModal();

    // Estado para responsividade da página principal
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [recorrenciasResponse, categoriasResponse] = await Promise.all([
                getRecurrences(),
                getCategorias()
            ]);
            setRecorrencias(recorrenciasResponse.results || recorrenciasResponse);
            setResumo(recorrenciasResponse.resumo_mensal || null);
            const catMap = new Map();
            categoriasResponse.forEach(cat => {
                catMap.set(cat.id, cat.tipo);
            });
            setCategoriaTipoMap(catMap);
        } catch (error) {
            console.error("Erro ao buscar dados da página:", error);
            messageApi.error(getErrorMessage(error, "Falha ao buscar os dados de recorrências."));
        } finally {
            setLoading(false);
        }
    }, [messageApi]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const [searchText, setSearchText] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [quickFilter, setQuickFilter] = useState('all');
    const [dateRange, setDateRange] = useState(null);

    const filteredRecorrencias = useMemo(() => {
        const hoje = dayjs().startOf('day');
        const searchLower = searchText.trim().toLowerCase();
        return recorrencias.filter(rec => {
            const tipo = categoriaTipoMap.get(rec.categoria);
            if (filterType !== 'all' && tipo !== filterType) return false;

            if (searchLower) {
                const valorFmt = parseFloat(rec.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
                const dataFmt = rec.proxima_ocorrencia ? dayjs(rec.proxima_ocorrencia).format('DD/MM/YYYY') : '';
                const matchesSearch =
                    rec.descricao.toLowerCase().includes(searchLower) ||
                    valorFmt.includes(searchLower) ||
                    String(rec.valor).replace('.', ',').includes(searchLower) ||
                    dataFmt.includes(searchLower) ||
                    (rec.categoria_nome && rec.categoria_nome.toLowerCase().includes(searchLower)) ||
                    (rec.target?.nome && rec.target.nome.toLowerCase().includes(searchLower));
                if (!matchesSearch) return false;
            }

            if (quickFilter === 'pausadas') return rec.ativa === false;
            if (quickFilter === 'encerradas') return !!rec.encerrada;
            if (quickFilter === 'cobrancas') return !!rec.eh_cobranca;

            if (['atrasadas', '7dias', 'mes'].includes(quickFilter)) {
                if (!rec.ativa || !rec.proxima_ocorrencia) return false;
                const d = dayjs(rec.proxima_ocorrencia).startOf('day');
                if (quickFilter === 'atrasadas' && !d.isBefore(hoje)) return false;
                if (quickFilter === '7dias' && (d.isBefore(hoje) || d.isAfter(hoje.add(7, 'day')))) return false;
                if (quickFilter === 'mes' && (d.month() !== hoje.month() || d.year() !== hoje.year())) return false;
            }

            if (dateRange && dateRange[0] && dateRange[1]) {
                if (!rec.proxima_ocorrencia) return false;
                const d = dayjs(rec.proxima_ocorrencia);
                if (d.isBefore(dateRange[0], 'day') || d.isAfter(dateRange[1], 'day')) return false;
            }

            return true;
        });
    }, [recorrencias, searchText, filterType, quickFilter, dateRange, categoriaTipoMap]);

    const openAgenda = useCallback(async (record) => {
        setAgenda({ open: true, loading: true, data: null, record });
        try {
            const data = await getRecurrenceAgenda(record.id);
            setAgenda({ open: true, loading: false, data, record });
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Não foi possível carregar a agenda.'));
            setAgenda({ open: false, loading: false, data: null, record: null });
        }
    }, [messageApi]);

    const handleAddNew = () => {
        setEditingRecord(null);
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        modal.confirm({
            title: 'Você tem certeza?',
            content: 'Esta ação irá excluir permanentemente esta regra de recorrência.',
            okText: 'Sim, excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteRecurrence(id);
                    messageApi.success('Recorrência excluída com sucesso!');
                    fetchData();
                } catch (error) {
                    console.error("Erro ao excluir recorrência:", error);
                    messageApi.error(getErrorMessage(error, "Falha ao excluir a recorrência."));
                }
            },
        });
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingRecord(null);
    };

    const handleModalFinish = async (values, shouldClose = true) => {
        const isEditing = !!editingRecord;
        const action = isEditing ? updateRecurrence : createRecurrence;
        const id = isEditing ? editingRecord.id : null;
        try {
            if (isEditing) {
                await action(id, values);
                messageApi.success('Recorrência atualizada com sucesso!');
            } else {
                await action(values);
                if (shouldClose) {
                    messageApi.success('Recorrência criada com sucesso!');
                } else {
                    messageApi.success('Recorrência criada! Adicione a próxima.');
                }
            }
            fetchData();
            if (shouldClose) {
                setIsModalVisible(false);
                setEditingRecord(null);
            }
            return Promise.resolve();
        } catch (error) {
            console.error("Erro ao salvar recorrência:", error);
            messageApi.error(getErrorMessage(error, "Erro ao salvar recorrência."));
            return Promise.reject(error);
        }
    };

    // Columns para Desktop
    const columns = [
        {
            title: 'Descrição',
            dataIndex: 'descricao',
            key: 'descricao',
            sorter: (a, b) => a.descricao.localeCompare(b.descricao),
            render: (descricao, record) => (
                <Space size={4} wrap>
                    <span>{descricao}</span>
                    {record.eh_cobranca && (
                        <Tooltip title="Cobrança recorrente: envia lembrete no WhatsApp com Pix a cada vencimento">
                            <Tag color="gold" icon={<BellOutlined />} style={{ marginInlineEnd: 0 }}>Cobrança</Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Valor',
            dataIndex: 'valor',
            key: 'valor',
            sorter: (a, b) => a.valor - b.valor,
            render: (valor, record) => {
                if (isPrivate) return '****';
                const tipo = categoriaTipoMap.get(record.categoria);
                const isReceita = tipo === 'receita';
                const color = isReceita ? 'green' : 'red';
                const Icon = isReceita ? ArrowUpOutlined : ArrowDownOutlined;
                return (
                    <Tooltip title={isReceita ? 'Receita' : 'Despesa'}>
                        <span style={{ color, whiteSpace: 'nowrap' }}>
                            <Icon /> {parseFloat(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                    </Tooltip>
                );
            }
        },
        { title: 'Categoria', dataIndex: 'categoria_nome', key: 'categoria' },
        {
            title: 'Frequência',
            key: 'frequencia',
            sorter: (a, b) => (FREQ_LABEL[a.frequencia] || '').localeCompare(FREQ_LABEL[b.frequencia] || ''),
            render: (_, record) => (
                <div style={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                    <div>{FREQ_LABEL[record.frequencia] || record.frequencia}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{freqDetalhe(record)}</Text>
                </div>
            ),
        },
        {
            title: 'Destino',
            dataIndex: 'target',
            key: 'target',
            render: (target) => (
                <>
                    {target.nome} <Tag color={target.tipo === 'conta' ? 'blue' : 'purple'}>{target.tipo === 'conta' ? 'Conta' : 'Cartão'}</Tag>
                </>
            )
        },
        {
            title: 'Próximo lançamento',
            key: 'proxima_ocorrencia',
            defaultSortOrder: 'ascend',
            sorter: (a, b) => proximaSortValue(a) - proximaSortValue(b),
            render: (_, record) => {
                if (record.ativa === false) return <Tag icon={<PauseCircleOutlined />}>Pausada</Tag>;
                if (record.encerrada || !record.proxima_ocorrencia) return <Tag icon={<StopOutlined />}>Encerrada</Tag>;
                const rel = relativoData(record.proxima_ocorrencia);
                const isCartao = record.target?.tipo !== 'conta';
                return (
                    <Tooltip title={regraTexto(record)}>
                        <div style={{ whiteSpace: 'nowrap', lineHeight: 1.2, cursor: 'pointer' }} onClick={() => openAgenda(record)}>
                            <div>{dayjs(record.proxima_ocorrencia).format('DD/MM/YYYY')}</div>
                            <Text type={rel.tone === 'secondary' ? 'secondary' : rel.tone} style={{ fontSize: 12 }}>
                                {isCartao ? 'na fatura · ' : ''}{rel.label}
                            </Text>
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Ações',
            key: 'acoes',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Ver agenda"><Button shape="circle" icon={<CalendarOutlined />} onClick={() => openAgenda(record)} /></Tooltip>
                    <Tooltip title="Editar"><Button shape="circle" icon={<EditOutlined />} onClick={() => handleEdit(record)} /></Tooltip>
                    <Tooltip title="Excluir"><Button shape="circle" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} /></Tooltip>
                </Space>
            ),
        },
    ];

    const hasActiveFilters = !!searchText.trim() || filterType !== 'all' || quickFilter !== 'all' || !!(dateRange && dateRange[0]);

    const customEmpty = recorrencias.length > 0 && hasActiveFilters ? (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Nenhuma recorrência corresponde aos filtros."
        >
            <Button onClick={() => { setSearchText(''); setFilterType('all'); setQuickFilter('all'); setDateRange(null); }}>
                Limpar filtros
            </Button>
        </Empty>
    ) : (
        <EmptyStateGuide
            title="Nenhuma recorrência encontrada."
            description="Automatize suas finanças cadastrando suas despesas e receitas mensais!"
            actionLabel="Criar primeira recorrência"
            onAction={handleAddNew}
        />
    );

    const resumoCard = resumo && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {[
                { titulo: 'Receitas fixas / mês', valor: resumo.receita_mensal, cor: '#28a745', Icon: ArrowUpOutlined },
                { titulo: 'Despesas fixas / mês', valor: resumo.despesa_mensal, cor: '#dc3545', Icon: ArrowDownOutlined },
                { titulo: 'Saldo recorrente / mês', valor: resumo.saldo_mensal, cor: parseFloat(resumo.saldo_mensal) >= 0 ? '#28a745' : '#dc3545', Icon: SwapOutlined },
            ].map(({ titulo, valor, cor, Icon }) => (
                <Col xs={24} sm={8} key={titulo}>
                    <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{titulo}</Text>
                        <div style={{ fontSize: 20, fontWeight: 600, color: cor, whiteSpace: 'nowrap' }}>
                            {isPrivate ? '****' : <><Icon style={{ fontSize: 14 }} /> {formatBRL(valor)}</>}
                        </div>
                    </Card>
                </Col>
            ))}
        </Row>
    );

    const quickFilterOptions = [
        { value: 'all', label: 'Todas' },
        { value: 'atrasadas', label: 'Atrasadas' },
        { value: '7dias', label: 'Próximos 7 dias' },
        { value: 'mes', label: 'Este mês' },
        { value: 'cobrancas', label: 'Cobranças' },
        { value: 'pausadas', label: 'Pausadas' },
        { value: 'encerradas', label: 'Encerradas' },
    ];

    const situacaoIcon = { paga: <CheckCircleOutlined style={{ color: '#28a745' }} />, pulada: <MinusCircleOutlined style={{ color: '#8c8c8c' }} />, alterada: <SwapOutlined style={{ color: '#1677ff' }} /> };
    const situacaoLabel = { paga: 'Paga', pulada: 'Pulada', alterada: 'Alterada' };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: '100%', overflowX: 'hidden' }}>
            {contextHolder}
            {modalContextHolder}

            {/* Cabeçalho */}
            <div style={{ marginBottom: 24 }}>
                <Breadcrumb items={[{ href: '/meu-desempenho', title: <HomeOutlined /> }, { title: 'Recorrências' }]} style={{ marginBottom: 8 }} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Central de Recorrências</Title>
                    <Button icon={<WalletOutlined />} onClick={() => navigate('/recorrencias/raio-x-assinaturas')}>
                        Raio-X de Assinaturas
                    </Button>
                </div>
            </div>

            {resumoCard}

            {/* Barra de Filtros e Ações */}
            <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 12,
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'flex-start',
                marginBottom: 24
            }}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: 8, flex: 1 }}>
                    <Input.Search
                        placeholder="Buscar descrição, valor, data..."
                        allowClear
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: isMobile ? '100%' : 260 }}
                    />
                    <Select
                        defaultValue="all"
                        style={{ width: isMobile ? '100%' : 170 }}
                        onChange={(value) => setFilterType(value)}
                        options={[
                            { value: 'all', label: 'Todos os Tipos' },
                            { value: 'receita', label: 'Apenas Receitas' },
                            { value: 'despesa', label: 'Apenas Despesas' },
                        ]}
                    />
                    <Select
                        value={quickFilter}
                        style={{ width: isMobile ? '100%' : 170 }}
                        onChange={setQuickFilter}
                        options={quickFilterOptions}
                    />
                    <DatePicker.RangePicker
                        format="DD/MM/YYYY"
                        allowClear
                        placeholder={['Próx. de', 'até']}
                        value={dateRange}
                        onChange={setDateRange}
                        style={{ width: isMobile ? '100%' : 260 }}
                    />
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew} size="large" block={isMobile}>
                    Nova Recorrência
                </Button>
            </div>

            {/* Conteúdo Principal: Lista (Mobile) ou Tabela (Desktop) */}
            {isMobile ? (
                <List
                    loading={loading}
                    dataSource={filteredRecorrencias}
                    locale={{ emptyText: customEmpty }}
                    renderItem={(item) => {
                        const tipo = categoriaTipoMap.get(item.categoria);
                        const isReceita = tipo === 'receita';
                        const color = isReceita ? 'green' : 'red';
                        const Icon = isReceita ? ArrowUpOutlined : ArrowDownOutlined;
                        const rel = item.proxima_ocorrencia ? relativoData(item.proxima_ocorrencia) : null;

                        let proximoNode;
                        if (item.ativa === false) proximoNode = <Tag icon={<PauseCircleOutlined />}>Pausada</Tag>;
                        else if (item.encerrada || !item.proxima_ocorrencia) proximoNode = <Tag icon={<StopOutlined />}>Encerrada</Tag>;
                        else proximoNode = (
                            <span>
                                <strong>{dayjs(item.proxima_ocorrencia).format('DD/MM/YYYY')}</strong>
                                {rel && <Text type={rel.tone === 'secondary' ? 'secondary' : rel.tone} style={{ fontSize: 12, marginLeft: 6 }}>{rel.label}</Text>}
                            </span>
                        );

                        return (
                            <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                                    <Text strong style={{ fontSize: 16 }}>{item.descricao}</Text>
                                    <Text strong style={{ color, fontSize: 16, whiteSpace: 'nowrap' }}>
                                        {isPrivate ? '****' : <><Icon /> {parseFloat(item.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</>}
                                    </Text>
                                </div>

                                <Space direction="vertical" style={{ width: '100%', fontSize: 13, color: '#666' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <Tag color={isReceita ? 'success' : 'error'}>{isReceita ? 'Receita' : 'Despesa'}</Tag>
                                        {item.eh_cobranca && <Tag color="gold" icon={<BellOutlined />}>Cobrança</Tag>}
                                        <span>{item.categoria_nome}</span>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                        <span>{FREQ_LABEL[item.frequencia] || item.frequencia} · {freqDetalhe(item)}</span>
                                        <span>
                                            {item.target.tipo === 'conta' ? <BankOutlined /> : <CreditCardOutlined />} {item.target.nome}
                                        </span>
                                    </div>

                                    <div><CalendarOutlined /> Próximo: {proximoNode}</div>
                                </Space>

                                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                                    <Button icon={<CalendarOutlined />} onClick={() => openAgenda(item)}>Agenda</Button>
                                    <Button icon={<EditOutlined />} onClick={() => handleEdit(item)} block>Editar</Button>
                                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(item.id)} block>Excluir</Button>
                                </div>
                            </Card>
                        );
                    }}
                />
            ) : (
                <Table
                    columns={columns}
                    dataSource={filteredRecorrencias}
                    loading={loading}
                    rowKey="id"
                    pagination={{ pageSize: 50 }}
                    locale={{ emptyText: customEmpty }}
                />
            )}

            <RecorrenciaModal
                visible={isModalVisible}
                onCancel={handleModalCancel}
                onFinish={handleModalFinish}
                initialData={editingRecord}
                isMobile={isMobile}
            />

            <Drawer
                title={agenda.record ? `Agenda · ${agenda.record.descricao}` : 'Agenda da recorrência'}
                open={agenda.open}
                onClose={() => setAgenda({ open: false, loading: false, data: null, record: null })}
                width={isMobile ? '100%' : 420}
            >
                {agenda.loading && <div style={{ textAlign: 'center', padding: 24 }}>Carregando…</div>}
                {!agenda.loading && agenda.data && (
                    <>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {agenda.data.frequencia_label}
                            {agenda.data.data_fim ? ` · até ${dayjs(agenda.data.data_fim).format('DD/MM/YYYY')}` : ''}
                            {agenda.data.numero_max_ocorrencias ? ` · máx. ${agenda.data.numero_max_ocorrencias}` : ''}
                        </Text>

                        {agenda.data.ativa === false && <div style={{ marginTop: 8 }}><Tag icon={<PauseCircleOutlined />}>Pausada</Tag></div>}
                        {agenda.data.encerrada && <div style={{ marginTop: 8 }}><Tag icon={<StopOutlined />}>Encerrada</Tag></div>}

                        <Divider orientation="left" plain style={{ marginTop: 16 }}>Próximas ocorrências</Divider>
                        {agenda.data.proximas.length === 0 ? (
                            <Text type="secondary">Nenhuma ocorrência futura.</Text>
                        ) : (
                            <List
                                size="small"
                                dataSource={agenda.data.proximas}
                                renderItem={(oc) => {
                                    const rel = relativoData(oc.data);
                                    return (
                                        <List.Item>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>
                                                    {dayjs(oc.data).format('DD/MM/YYYY')}{' '}
                                                    <Text type={rel.tone === 'secondary' ? 'secondary' : rel.tone} style={{ fontSize: 12 }}>({rel.label})</Text>
                                                </span>
                                                <span>{isPrivate ? '****' : formatBRL(oc.valor)}</span>
                                            </div>
                                        </List.Item>
                                    );
                                }}
                            />
                        )}

                        <Divider orientation="left" plain style={{ marginTop: 16 }}>Histórico</Divider>
                        {agenda.data.historico.length === 0 ? (
                            <Text type="secondary">Nada registrado ainda.</Text>
                        ) : (
                            <List
                                size="small"
                                dataSource={agenda.data.historico}
                                renderItem={(oc) => (
                                    <List.Item>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                            <span>{situacaoIcon[oc.situacao]} {dayjs(oc.data).format('DD/MM/YYYY')} · {situacaoLabel[oc.situacao] || oc.situacao}</span>
                                            <span>{isPrivate ? '****' : formatBRL(oc.valor)}</span>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        )}

                        <Button
                            type="primary"
                            block
                            style={{ marginTop: 24 }}
                            icon={<EditOutlined />}
                            onClick={() => { const r = agenda.record; setAgenda({ open: false, loading: false, data: null, record: null }); handleEdit(r); }}
                        >
                            Editar recorrência
                        </Button>
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default RecorrenciasPage;